import React, { useState, useEffect, useMemo } from 'react';
import { ChargerStatusResponse, ChargerDetail } from '../pages/reports/model/reports';
import Pagination from './Pagination';

interface Props {
  data: ChargerStatusResponse | null;
}

const PAGE_SIZE = 10;

const STATUS_FILTER_OPTIONS = [
  'All Statuses',
  'Online',
  'Offline',
  'Available',
  'Charging',
  'Faulted',
  'Unavailable',
  'Reserved',
  'Out-Of-Service',
] as const;

const matchesStatus = (value: string | undefined, statusFilter: string) =>
  String(value || '').toLowerCase() === statusFilter.toLowerCase();

const chargerHasStatus = (charger: ChargerDetail, statusFilter: string) => {
  if (!statusFilter || statusFilter === 'All Statuses') return true;
  if (matchesStatus(charger.live_status, statusFilter) || matchesStatus(charger.local_status, statusFilter)) {
    return true;
  }
  return (charger.connectors || []).some(
    (c) => matchesStatus(c.live_status, statusFilter) || matchesStatus(c.local_status, statusFilter)
  );
};

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    Available: 'bg-success',
    'Online': 'bg-success',
    'online': 'bg-success',
    'Active': 'bg-success',
    'active': 'bg-success',
    'In-Use': 'bg-success',
    'in-use': 'bg-success',
    Charging: 'bg-success',
    'charging': 'bg-success',
    Offline: 'bg-danger',
    Faulty: 'bg-danger',
    Faulted: 'bg-danger',
    'Out-Of-Service': 'bg-warning text-dark',
    Unavailable: 'bg-warning text-dark',
    Occupied: 'bg-info',
    Reserved: 'bg-warning text-dark',
    reserved: 'bg-warning text-dark',
    Unknown: 'bg-secondary',
  };
  return <span className={`badge ${map[status] || 'bg-secondary'}`}>{status}</span>;
};

const countReservedConnectors = (chargers: ChargerDetail[] = []) =>
  chargers.reduce(
    (sum, charger) =>
      sum +
      (charger.connectors || []).filter((c) =>
        String(c.live_status || c.local_status || '').toLowerCase() === 'reserved'
      ).length,
    0
  );

/** Returns a single searchable string for a charger row (includes connectors) */
const chargerSearchString = (c: ChargerDetail): string =>
  [
    c.charger_identifier,
    c.charger_type,
    c.station_name,
    c.station_address,
    c.vendor_name,
    c.live_status,
    ...(c.connectors || []).flatMap((conn) => [
      conn.connector_type,
      conn.live_status,
      conn.local_status,
      String(conn.connector_number ?? ''),
    ]),
  ]
    .join(' ')
    .toLowerCase();

const ChargerRow: React.FC<{
  charger: ChargerDetail;
  index: number;
  forceExpanded?: boolean;
}> = ({ charger, index, forceExpanded = false }) => {
  const [expanded, setExpanded] = useState(forceExpanded);

  useEffect(() => {
    if (forceExpanded) setExpanded(true);
  }, [forceExpanded]);

  return (
    <>
      <tr>
        <td>{index}</td>
        <td>
          <button
            className="btn btn-sm btn-outline-secondary me-2"
            onClick={() => setExpanded((v) => !v)}
            title="Toggle connectors"
            style={{ color: '#fc4f4f', fontWeight: '600' }}
          >
            <i className={`bi ${expanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
          </button>
          {charger.charger_identifier}
        </td>
        <td>{charger.charger_type}</td>
        <td>{charger.station_name}</td>
        <td className="text-muted small">{charger.station_address}</td>
        <td>{charger.vendor_name}</td>
        <td>{statusBadge(charger.live_status)}</td>
      </tr>
      {expanded && charger.connectors?.length > 0 && (
        <tr>
          <td colSpan={7} className="p-0">
            <table
              className="table table-sm table-bordered mb-0"
              style={{ background: '#f8f9fa' }}
            >
              <thead className="table-light">
                <tr>
                  <th className="ps-4">#</th>
                  <th>Type</th>
                  <th>Max Power</th>
                  <th>Price/kWh</th>
                  <th>Live Status</th>
                </tr>
              </thead>
              <tbody>
                {charger.connectors.map((c) => (
                  <tr key={c.connector_id}>
                    <td className="ps-4">{c.connector_number}</td>
                    <td>{c.connector_type}</td>
                    <td>{c.max_power_kw} kW</td>
                    <td>₹{c.price_per_kwh}/kWh</td>
                    <td>{statusBadge(c.live_status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
};

const ChargerTable: React.FC<Props> = ({ data }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All Statuses');

  // Reset page when data or filters change
  useEffect(() => { setPage(1); }, [data, search, statusFilter]);

  const chargers = data?.chargers ?? [];
  const summary = data?.summary;
  const cs = summary?.chargers;
  const conn = summary?.connectors;

  // Real-time client-side search + status filter (charger or any connector)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return chargers.filter((c) => {
      if (!chargerHasStatus(c, statusFilter)) return false;
      if (!q) return true;
      return chargerSearchString(c).includes(q);
    });
  }, [chargers, search, statusFilter]);

  const pagedChargers = filtered.slice((page - 1) * pageSize, page * pageSize);
  const autoExpandForStatus =
    statusFilter !== 'All Statuses' &&
    !['Online', 'Offline'].includes(statusFilter);

  if (!data) return null;

  return (
    <>
      {/* Charger Summary Cards */}
      {cs && (
        <div className="row gy-3 mb-4">
          {[
            { label: 'Total', value: cs.total, color: 'bg-secondary' },
            { label: 'Online', value: cs.online, color: 'bg-success' },
            { label: 'Offline', value: cs.offline, color: 'bg-dark' },
            { label: 'Out of Service', value: cs.out_of_service, color: 'bg-warning text-dark' },
          ].map((item) => (
            <div className="col-xl col-md-3 col-sm-4 col-6" key={item.label}>
              <div className="card text-center h-100">
                <div className="card-body py-3">
                  <div className={`badge ${item.color} mb-2`} style={{ fontSize: 11 }}>
                    {item.label}
                  </div>
                  <div className="fw-bold fs-4">{item.value}</div>
                  <div className="text-muted small">Chargers</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Connector Summary Cards */}
      {conn && (
        <div className="row gy-3 mb-4">
          {[
            { label: 'Total Connectors', value: conn.total, color: 'bg-secondary' },
            { label: 'Available', value: conn.available, color: 'bg-success' },
            { label: 'Charging', value: conn.charging, color: 'bg-primary' },
            { label: 'Faulted', value: conn.faulted, color: 'bg-danger' },
            { label: 'Unavailable', value: conn.unavailable, color: 'bg-warning text-dark' },
            {
              label: 'Reserved',
              value: conn.reserved ?? countReservedConnectors(chargers),
              color: 'bg-info text-dark',
            },
          ].map((item) => (
            <div className="col-xl col-md-3 col-sm-4 col-6" key={item.label}>
              <div className="card text-center h-100">
                <div className="card-body py-3">
                  <div className={`badge ${item.color} mb-2`} style={{ fontSize: 11 }}>
                    {item.label}
                  </div>
                  <div className="fw-bold fs-4">{item.value}</div>
                  <div className="text-muted small">Connectors</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Charger List with Search + Pagination */}
      <div className="x_panel">
        <div className="x_title">
          <h4>
            Charger List ({filtered.length}
            {filtered.length !== chargers.length && ` of ${chargers.length}`} total)
          </h4>
          <div className="clearfix" />
        </div>
        <div className="x_content">
          {/* Real-time search + status filter */}
          <div className="row mb-3 g-2">
            <div className="col-md-5">
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <i className="fas fa-search text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by charger ID, station, vendor, address, connector…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setSearch('')}
                    title="Clear search"
                  >
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                title="Filter by charger or connector live status"
              >
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {filtered.length > 0 ? (
            <>
              <div className="table-responsive">
                <table className="table table-striped table-hover table-bordered">
                  <thead className="table-dark">
                    <tr>
                      <th>#</th>
                      <th>Charger ID</th>
                      <th>Type</th>
                      <th>Station</th>
                      <th>Address</th>
                      <th>Vendor</th>
                      <th>Live Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedChargers.map((c, idx) => (
                      <ChargerRow
                        key={c.charger_id}
                        charger={c}
                        index={(page - 1) * pageSize + idx + 1}
                        forceExpanded={
                          autoExpandForStatus &&
                          (c.connectors || []).some(
                            (conn) =>
                              matchesStatus(conn.live_status, statusFilter) ||
                              matchesStatus(conn.local_status, statusFilter)
                          )
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={page}
                totalItems={filtered.length}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              />
            </>
          ) : (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-search fs-1 d-block mb-2" />
              {search || statusFilter !== 'All Statuses'
                ? `No chargers match the current search/status filter`
                : 'No chargers found.'}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ChargerTable;
